import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { FieldShell } from './FieldShell';
import type { FieldComponentProps } from './types';

interface Coords {
  latitude: number;
  longitude: number;
}

export const GeoCodeField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const [status, setStatus] = useState<'idle' | 'capturing' | 'ready' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const stored = (value as Coords | null) || null;

  useEffect(() => {
    if (stored) {
      setStatus('ready');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('error');
      setMessage('Location services are not available in this browser.');
      return;
    }
    setStatus('capturing');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setStatus('ready');
      },
      (err) => {
        setStatus('error');
        setMessage(err.message || 'Unable to capture your location.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60_000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FieldShell field={field} error={error || (status === 'error' ? message ?? undefined : undefined)}>
      <div className="flex items-center gap-2 text-sm text-rgf-gray-600">
        <MapPin className="h-4 w-4" />
        {status === 'capturing' && 'Capturing location…'}
        {status === 'ready' && stored && (
          <span>
            {stored.latitude.toFixed(5)}, {stored.longitude.toFixed(5)}
          </span>
        )}
        {status === 'idle' && 'Location not yet captured.'}
        {status === 'error' && (message || 'Location capture failed.')}
      </div>
    </FieldShell>
  );
};
